<template>
  <span :class="badgeClasses" @click="handleClick">
    <slot></slot>
    <button v-if="closable" type="button" class="ui-badge__close" @click.stop="handleClose">
      <Icon icon="mdi:close" />
    </button>
  </span>
</template>

<script lang="ts" setup>
import { Icon } from "@iconify/vue";
import { computed } from "vue";
import type { Size, Variant } from "./types";

interface Props {
  variant?: Variant | "info" | "light";
  size?: Size;
  rounded?: boolean;
  closable?: boolean;
  clickable?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  variant: "primary",
  size: "md",
  rounded: false,
  closable: false,
  clickable: false,
});

const emit = defineEmits<{
  click: [event: MouseEvent];
  close: [];
}>();

const badgeClasses = computed(() => {
  const classes = ["ui-badge"];
  classes.push(`ui-badge--${props.variant}`);
  classes.push(`ui-badge--${props.size}`);
  if (props.rounded) classes.push("ui-badge--rounded");
  if (props.clickable) classes.push("ui-badge--clickable");
  if (props.closable) classes.push("ui-badge--closable");
  return classes;
});

const handleClick = (event: MouseEvent) => {
  if (props.clickable) {
    emit("click", event);
  }
};

const handleClose = () => {
  emit("close");
};
</script>

<style scoped>
.ui-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-family: "Inter", sans-serif;
  font-weight: 500;
  white-space: nowrap;
  vertical-align: middle;
  transition: all 0.2s ease-in-out;
}

/* Sizes */
.ui-badge--xs {
  padding: 2px 6px;
  font-size: 0.65rem;
  border-radius: 6px;
}

.ui-badge--sm {
  padding: 3px 8px;
  font-size: 0.7rem;
  border-radius: 8px;
}

.ui-badge--md {
  padding: 5px 10px;
  font-size: 0.75rem;
  border-radius: 10px;
}

.ui-badge--lg {
  padding: 6px 12px;
  font-size: 0.82rem;
  border-radius: 12px;
}

.ui-badge--xl {
  padding: 8px 14px;
  font-size: 0.9rem;
  border-radius: 14px;
}

/* Variants */
.ui-badge--primary {
  background-color: #e8e7ff;
  color: #3e3efe;
}

.ui-badge--secondary {
  background-color: #f3f4f6;
  color: #374151;
}

.ui-badge--success {
  background-color: #d1fae5;
  color: #065f46;
}

.ui-badge--warning {
  background-color: #fef3c7;
  color: #92400e;
}

.ui-badge--danger {
  background-color: #fee2e2;
  color: #991b1b;
}

.ui-badge--info {
  background-color: #dbeafe;
  color: #1e40af;
}

.ui-badge--light {
  background-color: #f9fafb;
  color: #111827;
  border: 1px solid #e5e7eb;
}

/* States */
.ui-badge--rounded {
  border-radius: 9999px;
}

.ui-badge--clickable {
  cursor: pointer;
}

.ui-badge--clickable:hover {
  opacity: 0.85;
  transform: translateY(-1px);
}

.ui-badge--closable {
  padding-right: 6px;
}

/* Close button */
.ui-badge__close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  padding: 0;
  margin-left: 2px;
  cursor: pointer;
  color: currentColor;
  opacity: 0.7;
  transition: opacity 0.2s ease-in-out;
}

.ui-badge__close:hover {
  opacity: 1;
}

.ui-badge__close .iconify {
  font-size: 12px;
}
</style>
